import React, { memo, useCallback, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { getCurrentSemanticMap } from "Slices/semanticMaps";
import {
  getMissionOperatorFleetIdForSubscribe,
  getMissionOperatorPickedFleetId,
  getOperatorMissionStatus,
  getIsOperatorPanelOccupiedForPickedStation,
  setMissionOperatorFleetId,
} from "Slices/operatorData";
import { setCurrentFleetId } from "Slices/missionPlanner";
import { ReactComponent as ConversionIcon } from "Components/common/assets/select-station-icon.svg";

import VehicleMissionStatusWithCustomStyles from "./components/VehicleWayStatus/VehicleMissionStatusWrapper";
import StationPickerWithCustomStyles from "./components/StationPicker/StationPickerWrapper";
import StationActionsWithCustomStyles from "./components/StationActions/StationActionsWrapper";
import { ZoneSelector, ZoneSelectorRef } from "./components/ZoneSelector";
import { StatusTabs } from "./components/StatusTabs/StatusTabs";

import {
  NewStationViewWrapperStyled,
  HeaderStyled,
  TopControlPanelStyled,
  LeftColumnStyled,
  ZoneBlockStyled,
  ZoneInfoStyled,
  ZoneLabelStyled,
  ZoneNameRowStyled,
  ZoneNameStyled,
  SelectorsBlockStyled,
  MiddleColumnStyled,
  RightColumnStyled,
  ActionCardPlaceholderStyled,
} from "./styles";

export const StationView = memo(() => {
  const currentSemanticMap = useSelector(getCurrentSemanticMap);
  const operatorMissionStatus = useSelector(getOperatorMissionStatus);
  const isPanelOccupiedForPickedStation = useSelector(
    getIsOperatorPanelOccupiedForPickedStation
  );
  const operatorFleetIdForSubscribe = useSelector(
    getMissionOperatorFleetIdForSubscribe
  );
  const pickedFleetId = useSelector(getMissionOperatorPickedFleetId);
  const dispatch = useDispatch();
  const zoneSelectorToggleRef = useRef<ZoneSelectorRef>(null);

  const handleSelectedFleet = useCallback(
    (fleetId: string | null) => {
      dispatch(setMissionOperatorFleetId(fleetId));
      // setCurrentFleetId will be synced via useEffect when pickedFleetId changes
    },
    [dispatch]
  );

  const handleZoneNameClick = useCallback(() => {
    zoneSelectorToggleRef.current?.toggle();
  }, []);

  const handleZoneNameKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleZoneNameClick();
      }
    },
    [handleZoneNameClick]
  );

  const initFleetId =
    operatorMissionStatus &&
    operatorMissionStatus !== "NO_MISSION" &&
    operatorFleetIdForSubscribe
      ? operatorFleetIdForSubscribe
      : "";

  // Sync fleetId with missionPlanner when fleet selection changes
  // Uses operatorFleetIdForSubscribe when mission is active, otherwise uses pickedFleetId
  // This handles both manual selection (pickedFleetId) and external updates (operatorFleetIdForSubscribe)
  useEffect(() => {
    const fleetIdToSync =
      operatorMissionStatus && operatorMissionStatus !== "NO_MISSION"
        ? operatorFleetIdForSubscribe
        : pickedFleetId;

    dispatch(setCurrentFleetId(fleetIdToSync || null));
  }, [
    operatorFleetIdForSubscribe,
    pickedFleetId,
    operatorMissionStatus,
    dispatch,
  ]);

  // Mark the document root with the route while StationView is mounted, so the
  // NavigationBar can hide itself in fullscreen via CSS without subscribing to
  // route state. Paired with FullscreenToggle's `html[data-fullscreen="true"]`.
  useEffect(() => {
    document.documentElement.dataset.route = "station";
    return () => {
      if (document.documentElement.dataset.route === "station") {
        delete document.documentElement.dataset.route;
      }
    };
  }, []);

  return (
    <NewStationViewWrapperStyled>
      <HeaderStyled>
        <TopControlPanelStyled>
          <LeftColumnStyled>
            <ZoneBlockStyled>
              <ZoneInfoStyled>
                <ConversionIcon />
                <div>
                  <ZoneLabelStyled>Zone of the customer</ZoneLabelStyled>
                  <ZoneNameRowStyled>
                    <ZoneNameStyled
                      role="button"
                      tabIndex={0}
                      onClick={handleZoneNameClick}
                      onKeyDown={handleZoneNameKeyDown}
                      aria-label={`Zone: ${
                        currentSemanticMap?.name ?? "Not selected"
                      }. Click to change zone.`}
                    >
                      {currentSemanticMap?.name ?? ""}
                    </ZoneNameStyled>
                    <ZoneSelector ref={zoneSelectorToggleRef} />
                  </ZoneNameRowStyled>
                </div>
              </ZoneInfoStyled>
            </ZoneBlockStyled>
            <SelectorsBlockStyled>
              <StationPickerWithCustomStyles />
            </SelectorsBlockStyled>
          </LeftColumnStyled>

          <MiddleColumnStyled>
            <ActionCardPlaceholderStyled>
              <StationActionsWithCustomStyles
                initFleetId={initFleetId}
                isPickerDisabled={isPanelOccupiedForPickedStation}
                handleSelectedFleet={handleSelectedFleet}
                shouldUseSavedFleet={true}
              />
            </ActionCardPlaceholderStyled>
          </MiddleColumnStyled>

          <RightColumnStyled>
            <VehicleMissionStatusWithCustomStyles />
          </RightColumnStyled>
        </TopControlPanelStyled>
      </HeaderStyled>
      <StatusTabs />
    </NewStationViewWrapperStyled>
  );
});

StationView.displayName = "StationView";
